import { Injectable, signal, computed } from '@angular/core';
import { StepStatus } from '../enums/campaign-setup.enum';
import { CampaignTypeEnum, CampaignTypeEnumm } from '../interfaces/common.interfaces';

@Injectable({
  providedIn: 'root',
})
export class CampaignSetupSignalService {
  private readonly _currentStep = signal<1 | 2 | 3 | 4>(1);
  private readonly _selectedCampaignType = signal<CampaignTypeEnumm | null>(null);
  private readonly _photoCount = signal<number>(0);
  private readonly _formConfigCache = new Map<string, { lockedFields: any[]; suggestedFields: any[] }>();
  private _customFormItems: { label: string; value: number }[] | null = null;
  private _contactList: { label: string; value: number }[] | null = null;

  readonly currentStep = computed(() => this._currentStep());
  readonly selectedCampaignType = computed(() => this._selectedCampaignType());
  readonly photoCount = computed(() => this._photoCount());

  readonly stepStatuses = computed<Record<number, StepStatus>>(() => {
    const current = this._currentStep();
    const status = (step: number): StepStatus => {
      if (step === current) return StepStatus.ACTIVE;
      if (step < current) return StepStatus.COMPLETE;
      return StepStatus.PENDING;
    };
    return { 1: status(1), 2: status(2), 3: status(3), 4: status(4) };
  });

  nextStep(): void {
    const current = this._currentStep();
    if (current < 4) {
      this._currentStep.set((current + 1) as 1 | 2 | 3 | 4);
      window.scrollTo({ top: 0 });
    }
  }

  prevStep(): void {
    const current = this._currentStep();
    if (current > 1) {
      this._currentStep.set((current - 1) as 1 | 2 | 3 | 4);
      window.scrollTo({ top: 0 });
    }
  }

  setSelectedCampaignType(type: CampaignTypeEnumm): void {
    if (this._selectedCampaignType() !== type) {
      this._formConfigCache.clear();
    }
    this._selectedCampaignType.set(type);
  }

  getCachedFormConfig(typeId: string): { lockedFields: any[]; suggestedFields: any[] } | undefined {
    return this._formConfigCache.get(typeId);
  }

  setCampaignFormConfig(typeId: string, config: { lockedFields: any[]; suggestedFields: any[] }): void {
    this._formConfigCache.set(typeId, config);
  }

  getCachedCustomFormItems(): { label: string; value: number }[] | null {
    return this._customFormItems;
  }

  setCustomFormItems(items: { label: string; value: number }[]): void {
    this._customFormItems = items;
  }

  getCachedContactList(): { label: string; value: number }[] | null {
    return this._contactList;
  }

  setContactList(items: { label: string; value: number }[]): void {
    this._contactList = items;
  }

  setPhotoCount(count: number): void {
    this._photoCount.set(count);
  }

  reset(): void {
    this._currentStep.set(1);
    this._selectedCampaignType.set(null);
    this._photoCount.set(0);
    this._formConfigCache.clear();
    this._customFormItems = null;
    this._contactList = null;
  }
}
